import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PauseOrdersToggle — self-serve retailer control to stop accepting new
 * consumer orders without going offline. Items stay visible, but checkout
 * returns HTTP 409 with the paused reason for any DAC placing an order.
 *
 * Cluster 6 P5.
 */
export default function PauseOrdersToggle() {
  const [paused, setPaused] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/retailer/settings/pause-status');
      setPaused(!!res.data?.orders_paused);
      setReason(res.data?.paused_reason || '');
    } catch (_) { /* silently ignore */ }
    finally { setInitialLoad(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const persist = async (nextPaused, nextReason) => {
    setBusy(true);
    try {
      await api.put('/retailer/settings/pause-orders', { paused: nextPaused, reason: nextReason });
      setPaused(nextPaused);
      if (nextPaused) {
        toast.success('New orders paused. Existing orders continue as normal.');
      } else {
        toast.success('Orders resumed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    } finally { setBusy(false); }
  };

  if (initialLoad) return null;

  return (
    <Card data-testid="pause-orders-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {paused
            ? <PauseCircle className="w-5 h-5 text-amber-500" />
            : <PlayCircle className="w-5 h-5 text-emerald-500" />}
          {paused ? 'New orders PAUSED' : 'Accepting new orders'}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Toggle to temporarily stop new consumer orders (e.g. rush hour, stockroom clear-out).
          Items remain visible on the storefront; existing orders are unaffected.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Pause new orders</span>
          <Switch
            checked={paused}
            onCheckedChange={(next) => persist(next, next ? reason : '')}
            disabled={busy}
            data-testid="pause-orders-switch"
          />
        </div>
        {paused && (
          <div className="space-y-2">
            <label className="text-xs text-slate-500">Reason shown to consumers (optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Restocking until 3 PM"
              maxLength={200}
              data-testid="pause-orders-reason"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => persist(true, reason)}
              disabled={busy}
              data-testid="pause-orders-save-reason"
            >
              Save reason
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
